/**
 * The event flows' Draft: everything the three steps collect, the rules for
 * how the title and category follow the place, and the create payload. The
 * create flow starts it blank or from an idea, the edit flow from a saved
 * event (its patch is `EventEditFlow/core/editState`). One Draft for the whole
 * flow, so going back keeps what was entered. Pure — the page renders it and
 * wires it to the mutation. Mirrors iOS and Android `EventDraft` so the
 * clients agree on the rules.
 */

import { DEFAULT_EVENT_CATEGORY } from '@/constants/eventCategories'
import { MAX_ATTACHMENTS } from '@components/Attachments/attachmentTypes'
import { addLinkRow, compactLinks, linkUrls, removeLinkRow, sameLinks, setLinkRow } from '@lib/links'
import {
	LocationLike,
	PLACE_NAME_MAX,
	PlaceDraft,
	placeFromLocation,
	placeName,
	placeToPayload,
	placeTyped,
	samePlacePayload,
} from '@lib/place'
import { EventCategory, GetFileType, GetIdeaResponse, PostEventType } from '@orbit/api-types'
import { Event } from '@types'
import { categoryFromGoogleTypes } from './categoryGuess'

/** The contract's cap on an event's links (pinned by `contract.test.ts`). */
export const MAX_EVENT_LINKS = 5
/** The cap on an event name — the one a place's name is prefilled to, as the event editor and iOS hold it. */
export const EVENT_TITLE_MAX = PLACE_NAME_MAX

/**
 * Where the title came from, which decides whether picking a place may
 * overwrite it: only a typed title is safe from the place. `savedPlace` is a
 * saved event's name that is its place's name: a new place renames it, but
 * clearing the place keeps it — a saved name isn't the flow's to throw away.
 */
export type TitleSource = 'none' | 'user' | 'place' | 'idea' | 'savedPlace'

/**
 * Where the category came from, which decides whether picking a place may
 * change it: only a guess follows the place. `none` is the default Other;
 * `chosen` is a pick, or an idea's own category, and stops the guessing.
 */
export type CategorySource = 'none' | 'guessed' | 'chosen'

export interface EventCreateDraft {
	/** The picked or typed place, or null for none. */
	place: PlaceDraft | null
	title: string
	titleSource: TitleSource
	category: EventCategory
	categorySource: CategorySource
	/** Zero-based trip day. */
	tripDayIndex: number
	/** Time of day as `HH:MM`, or null for an event with no set time. */
	startTime: string | null
	/** Files picked in the flow; uploaded once the event is created or saved. */
	files: File[]
	/** Files already on a saved event (the edit flow); one removed here is deleted on Save. */
	savedFiles: GetFileType[]
	/** One row per link, blanks included — blank rows are dropped on the way out. */
	links: string[]
	notes: string
	/** Set when the event is being made out of an idea. */
	createdFromIdeaId: number | null
}

const capTitle = (text: string): string => text.substring(0, EVENT_TITLE_MAX)

export const emptyDraft = (tripDayIndex: number): EventCreateDraft => ({
	place: null,
	title: '',
	titleSource: 'none',
	category: DEFAULT_EVENT_CATEGORY,
	categorySource: 'none',
	tripDayIndex,
	startTime: null,
	files: [],
	savedFiles: [],
	links: [],
	notes: '',
	createdFromIdeaId: null,
})

/** A saved location as a place: a Google one whole, so the event keeps its map pin; a typed one as its text. */
const savedPlace = (location: LocationLike | null | undefined): PlaceDraft | null => {
	if (!location) return null
	if (location.googlePlaceId) return placeFromLocation(location)
	const text = (location.primaryText || location.fullText).trim()
	return text ? placeTyped(text) : null
}

/**
 * An idea taken into the itinerary. Set directly rather than through
 * `setPlace`: the idea's own name and category win over anything its place
 * would suggest.
 */
export const draftFromIdea = (idea: GetIdeaResponse, tripDayIndex: number): EventCreateDraft => {
	const title = capTitle(idea.name)
	const category = idea.category || DEFAULT_EVENT_CATEGORY
	return {
		...emptyDraft(tripDayIndex),
		place: savedPlace(idea.location),
		title,
		titleSource: title.trim() ? 'idea' : 'none',
		category,
		categorySource: category === DEFAULT_EVENT_CATEGORY ? 'none' : 'chosen',
		links: compactLinks(linkUrls(idea.links)).slice(0, MAX_EVENT_LINKS),
		notes: idea.description ?? '',
		createdFromIdeaId: idea.id,
	}
}

/**
 * A saved event, for the edit flow. Its name follows a new place only while
 * it is still the saved place's name (`savedPlace`). A category other than
 * Other was chosen, like an idea's, so it sticks. Links aren't capped:
 * dropping one here would delete it on save.
 */
export const draftFromEvent = (event: Event, savedFiles: GetFileType[]): EventCreateDraft => {
	const place = savedPlace(event.location)
	const title = event.name
	let titleSource: TitleSource = 'user'
	if (!title.trim()) titleSource = 'none'
	else if (place && title.trim() === placeName(place)) titleSource = 'savedPlace'
	const category = event.category || DEFAULT_EVENT_CATEGORY
	return {
		...emptyDraft(event.tripDayIndex),
		place,
		title,
		titleSource,
		category,
		categorySource: category === DEFAULT_EVENT_CATEGORY ? 'none' : 'chosen',
		startTime: event.startTime,
		savedFiles,
		links: linkUrls(event.tripEventLinks),
		notes: event.description ?? '',
	}
}

// MARK: What — place, title, category

/**
 * Sets or clears the place, carrying the title and the category guess with
 * it. A title the user typed is never touched; one the previous place filled
 * in follows the place, and is cleared along with it.
 */
export const setPlace = (draft: EventCreateDraft, place: PlaceDraft | null): EventCreateDraft => {
	if (!place) {
		const dropsTitle = draft.titleSource === 'place'
		const dropsGuess = draft.categorySource === 'guessed'
		return {
			...draft,
			place: null,
			...(dropsTitle && { title: '', titleSource: 'none' as const }),
			...(dropsGuess && { category: DEFAULT_EVENT_CATEGORY, categorySource: 'none' as const }),
		}
	}

	let next: EventCreateDraft = { ...draft, place }
	if (draft.titleSource !== 'user') {
		const title = placeName(place)
		next = { ...next, title, titleSource: title ? 'place' : 'none' }
	}
	if (draft.categorySource === 'chosen') return next

	const guess = categoryFromGoogleTypes(place.googlePlace?.types)
	if (guess) return { ...next, category: guess, categorySource: 'guessed' }
	// The old place's guess doesn't describe the new one.
	if (draft.categorySource === 'guessed') return { ...next, category: DEFAULT_EVENT_CATEGORY, categorySource: 'none' }
	return next
}

/** The user typed in the title field. Clearing it hands the title back to the place, so picking one names the event again. */
export const setTitle = (draft: EventCreateDraft, text: string): EventCreateDraft => {
	const title = capTitle(text)
	return { ...draft, title, titleSource: title.trim() ? 'user' : 'none' }
}

/** A pick by hand: it sticks, and later places stop guessing. */
export const chooseCategory = (draft: EventCreateDraft, category: EventCategory): EventCreateDraft => ({
	...draft,
	category,
	categorySource: 'chosen',
})

/** What's Next: the event needs a name, and a place provides one. */
export const canLeaveWhatStep = (draft: EventCreateDraft): boolean =>
	draft.place !== null || draft.title.trim() !== ''

/** The name the event is created with: the title, or the place's name when the user cleared the title but kept the place. */
export const eventName = (draft: EventCreateDraft): string =>
	draft.title.trim() || (draft.place ? placeName(draft.place) : '')

// MARK: When — day, start time

export const setDay = (draft: EventCreateDraft, tripDayIndex: number): EventCreateDraft => ({ ...draft, tripDayIndex })

export const setStartTime = (draft: EventCreateDraft, startTime: string | null): EventCreateDraft => ({
	...draft,
	startTime,
})

// MARK: Extras — files, links, notes

/** How many more files the event can take, its saved ones counted. */
export const remainingFiles = (draft: EventCreateDraft): number =>
	MAX_ATTACHMENTS - draft.savedFiles.length - draft.files.length

/** Whether a pick of `count` files fits under the cap. One that would run past it is refused whole, as every attachment picker refuses it. */
export const canAddFiles = (draft: EventCreateDraft, count: number): boolean => count <= remainingFiles(draft)

export const canAddFile = (draft: EventCreateDraft): boolean => canAddFiles(draft, 1)

/** Appends a pick of files. A pick past the cap is refused whole, leaving the Draft as it was. */
export const addFiles = (draft: EventCreateDraft, files: File[]): EventCreateDraft =>
	canAddFiles(draft, files.length) ? { ...draft, files: [...draft.files, ...files] } : draft

export const removeFile = (draft: EventCreateDraft, index: number): EventCreateDraft => ({
	...draft,
	files: draft.files.filter((_, i) => i !== index),
})

/** Takes a saved file off the event; it is deleted on Save. */
export const removeSavedFile = (draft: EventCreateDraft, fileId: string): EventCreateDraft => ({
	...draft,
	savedFiles: draft.savedFiles.filter(file => file.id !== fileId),
})

export const canAddLink = (draft: EventCreateDraft): boolean => draft.links.length < MAX_EVENT_LINKS

/** A new empty row for the user to fill in, up to the cap. */
export const addLink = (draft: EventCreateDraft): EventCreateDraft =>
	canAddLink(draft) ? { ...draft, links: addLinkRow(draft.links) } : draft

export const setLink = (draft: EventCreateDraft, index: number, text: string): EventCreateDraft => ({
	...draft,
	links: setLinkRow(draft.links, index, text),
})

export const removeLink = (draft: EventCreateDraft, index: number): EventCreateDraft => ({
	...draft,
	links: removeLinkRow(draft.links, index),
})

/** The links as sent: trimmed, blanks dropped. */
export const filledLinks = (draft: EventCreateDraft): string[] => compactLinks(draft.links)

export const setNotes = (draft: EventCreateDraft, notes: string): EventCreateDraft => ({ ...draft, notes })

// MARK: Discarding

const samePlace = (a: PlaceDraft | null, b: PlaceDraft | null): boolean =>
	samePlacePayload(a && placeToPayload(a), b && placeToPayload(b))

/** Whether anything was entered beyond what the flow opened with — when closing asks before throwing the Draft away. */
export const hasChanges = (draft: EventCreateDraft, original: EventCreateDraft): boolean =>
	!samePlace(draft.place, original.place) ||
	draft.title !== original.title ||
	draft.category !== original.category ||
	draft.tripDayIndex !== original.tripDayIndex ||
	draft.startTime !== original.startTime ||
	draft.files.length !== original.files.length ||
	!sameLinks(filledLinks(draft), filledLinks(original)) ||
	draft.notes !== original.notes

// MARK: Request

export const toCreatePayload = (draft: EventCreateDraft): PostEventType => {
	const description = draft.notes.trim()
	const location = draft.place ? placeToPayload(draft.place) : undefined
	return {
		tripDayIndex: draft.tripDayIndex,
		name: eventName(draft),
		...(description && { description }),
		...(draft.startTime && { startTime: draft.startTime }),
		...(location && { location }),
		tripEventLinks: filledLinks(draft),
		tags: [],
		category: draft.category,
		...(draft.createdFromIdeaId !== null && { createdFromIdeaId: draft.createdFromIdeaId }),
	}
}
